import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { majorContractsService } from "@shared/services/majorContracts";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import LoadingButton from '@shared/components/LoadingButton';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

export default function MajorContractForm({ onClose, onConfirm, editData, formProps }) {
    const { t } = useTranslation();
    const validationSchema = yup.object({
        reasonKt: yup.string().required(t('common.required')),
        description: yup.string().required(t('common.required')),
        code: yup.string().required(t('common.required')),
        hrNssiReasonsId: yup.string().required(t('common.required')),
        termInMonts: yup.string().required(t('common.required')).matches(/^\d+$/, t('common.onlyNumbers')).default('0'),
        termInYears: yup.string().required(t('common.required')).matches(/^\d+$/, t('common.onlyNumbers')).default('0'),
        termDescription: yup.string().required(t('common.required')),
    });
    const { modalRef } = useFormHook({}, onClose);
    const { register, handleSubmit, setValue, watch, reset, formState: { errors } } =
        useForm({
            resolver: yupResolver(validationSchema),
            defaultValues: {
                active: true
            },
            ...formProps
        });
    const [hrNssiReasons, setHrNssiReasons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);


    useEffect(() => {
        const fetchHrNssiReasons = async () => {
            const data = await majorContractsService.getHrNssiReasons();
            setHrNssiReasons(data);
        };
        fetchHrNssiReasons();
    }, []);

    // Fetch data if editing
    useEffect(() => {
        const fetchMajorContractData = async () => {
            if (editData && editData.id) {
                try {
                    setFetchingData(true);
                    const data = await majorContractsService.getMajorContractById(editData.id);
                    
                    setValue('reasonKt', data.reasonKt || '');
                    setValue('description', data.description || '');
                    setValue('code', data.code || '');
                    setValue('hrNssiReasonsId', data.hrNssiReasonsId || '');
                    setValue('termInMonts', data.termInMonts !== undefined && data.termInMonts !== null ? data.termInMonts.toString() : '0');
                    setValue('termInYears', data.termInYears !== undefined && data.termInYears !== null ? data.termInYears.toString() : '0');
                    setValue('termDescription', data.termDescription || '');
                    setValue('active', data.active || false);
                } catch (error) {
                    console.error('Error fetching major contract data:', error);
                } finally {
                    setFetchingData(false);
                }
            } else {
                reset({
                    termInMonts: '0',
                    termInYears: '0',
                    active: true
                });
            }
        };

        fetchMajorContractData();
    }, [editData, setValue, reset]);


    const submitForm = async (data) => {
        try {
            setLoading(true);
            const formData = {
                reasonKt: data.reasonKt,
                description: data.description,
                code: data.code,
                hrNssiReasonsId: data.hrNssiReasonsId,
                termInMonts: data.termInMonts || '0',
                termInYears: data.termInYears || '0',
                termDescription: data.termDescription,
                active: data.active
            };
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        } finally {
            setLoading(false);
        }
    }
    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} {t('staff.majorContracts.title')}
                        </h4>
                    </div>
                    <form id="majorContractForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                        <div className="form-sections">
                            <div className="form-section">
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.reasonKt')}<span className="required">*</span></span>
                                    <input type="text"
                                        maxLength={120}
                                        className={`form-control ${errors.reasonKt ? 'input-error' : ''}`}
                                        placeholder={errors.reasonKt ? errors.reasonKt.message : t('common.required')}
                                        {...register('reasonKt')} />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.description')}<span className="required">*</span></span>
                                    <input type="text"
                                        maxLength={120}
                                        className={`form-control ${errors.description ? 'input-error' : ''}`}
                                        placeholder={errors.description ? errors.description.message : t('common.required')}
                                        {...register('description')} />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.code')}<span className="required">*</span></span>
                                    <input type="text"
                                        maxLength={10}
                                        className={`form-control ${errors.code ? 'input-error' : ''}`}
                                        placeholder={errors.code ? errors.code.message : t('common.required')}
                                        {...register('code')} />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.hrNssiReasonsId')}<span className="required">*</span></span>
                                    <select className={`form-control ${errors.hrNssiReasonsId ? 'input-error' : ''}`}
                                        placeholder={errors.hrNssiReasonsId ? errors.hrNssiReasonsId.message : t('common.required')}
                                        {...register('hrNssiReasonsId')}>
                                        <option value="">{t('common.select')}</option>
                                        {hrNssiReasons.map((reason) => (
                                            <option key={reason.id} value={reason.id}>{reason.reasonId} - {reason.description}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.termInMonts')}<span className="required">*</span></span>
                                    <input 
                                        type="text" 
                                        maxLength={5} 
                                        className={`form-control ${errors.termInMonts ? 'input-error' : ''}`}
                                        placeholder={errors.termInMonts ? errors.termInMonts.message : '0'}
                                        onInput={(e) => {
                                            e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                        }}
                                        {...register('termInMonts')} 
                                    />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.termInYears')}<span className="required">*</span></span>
                                    <input 
                                        type="text" 
                                        maxLength={5} 
                                        className={`form-control ${errors.termInYears ? 'input-error' : ''}`}
                                        placeholder={errors.termInYears ? errors.termInYears.message : '0'}
                                        onInput={(e) => {
                                            e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                        }}
                                        {...register('termInYears')} 
                                    />
                                </div>
                                <div className="form-group">
                                    <span>{t('staff.majorContracts.table.headers.termInWords')}<span className="required">*</span></span>
                                    <input type="text"
                                        maxLength={120}
                                        className={`form-control ${errors.termDescription ? 'input-error' : ''}`}
                                        placeholder={errors.termDescription ? errors.termDescription.message : t('common.required')}
                                        {...register('termDescription')} />
                                </div>
                                <div className="form-group">
                                    <div className="checkbox-wrapper-start title-checkbox">
                                        <input
                                            type="checkbox"
                                            id="active"
                                            {...register('active')}
                                        />
                                        <label htmlFor="active">{t('staff.majorContracts.table.headers.active')}</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="majorContractForm" className="btn btn-primary" loading={loading}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}